import React, { useState, useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";
import validate from "validate.js";
import { Modal, Form, Row, Col } from "react-bootstrap";
import { CKEditor } from "@ckeditor/ckeditor5-react";
import ClassicEditor from "@ckeditor/ckeditor5-build-classic";
import { CK_CONFIGS } from "configs/ckEditor";
import DatePicker from "react-datepicker";
import Select from "react-select";
import {
  getIncidentFormValues,
  userFetch,
  hideIncidentValidationError,
  addIncidentPreventionAction,
  incidentPreventionActionListFetch,
  updateIncidentPreventionAction,
  hideIncidentPreventionActionValidationError,
} from "actions";
import { scopesListFetch } from "actions";
import { isEmpty } from "lodash";

const schema = {
  action_title: {
    presence: { allowEmpty: false, message: "^Title of Action is Required" },
  },
  implementation_time: {
    presence: {
      allowEmpty: false,
      message: "^Action Date & Time is required",
    },
  },
};

const AddPreventiveActionsModal = ({
  show,
  handleClose,
  incidentId,
  incidentTimezone,
  previousData,
  onNext,
  onBack,
  loadFromBack,
  setLoadFromBack,
}) => {
  const dispatch = useDispatch();


  const incidentState = useSelector((state) => state.incidentState);
  const incidentPreventionActionState = useSelector(
    (state) => state.incidentPreventionActionState
  );
  const session = useSelector((state) => state.session);
  const userList = useSelector((state) => state.commiteeState.getUser);
  const [incidentDateTime, setIncidentDateTime] = useState(new Date());

  const [severityLevelList, setSeverityLevelList] = useState([]);
  const [priorityLevelValue, setPriorityLevelValue] = useState(null);
  const [incidentPreventionActionRecord, setIncidentPreventionActionRecord] =
    useState({});
  const [selectedUser, setSelectedUser] = useState();


  const [formState, setFormState] = useState({
    isValid: false,
    values: {
      object_viewed_id: session.current_page_permissions?.object_id || "",
      action_title: "",
      action_description: "",
      implementation_time: incidentDateTime,
    },
    touched: {
      object_viewed_id: true,
    },
    errors: {},
  });

  useEffect(() => {
    if (!isEmpty(incidentPreventionActionState.validation_error)) {
      const errors = incidentPreventionActionState.validation_error;
      setFormState((formState) => ({
        ...formState,
        isValid: errors ? false : true,
        errors: errors || {},
      }));
    }
  }, [incidentPreventionActionState.validation_error]);

  const valueNames = ["Severity_Level"];

  useEffect(() => {
    if (show) {
      dispatch(getIncidentFormValues(valueNames));
    }
  }, [dispatch, show]);

  useEffect(() => {
    dispatch(userFetch());
  }, [dispatch]);

  useEffect(() => {
    dispatch(scopesListFetch());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const recordWrapper =
      incidentPreventionActionState.incidentPreventionActionRecord;


     

    const parsedDate = recordWrapper.implementation_time
      ? new Date(recordWrapper.implementation_time + "Z")
      : new Date();

    setIncidentDateTime(parsedDate);

    if (recordWrapper) {
      setIncidentPreventionActionRecord(recordWrapper);

      // Populate Priority Level
      if (
        recordWrapper.priority_level_relation &&
        recordWrapper.priority_level_relation.name === "Severity_Level"
      ) {
        setPriorityLevelValue({
          value: recordWrapper.priority_level_relation.id,
          label: (
            <div style={{ display: "flex", alignItems: "center" }}>
              <img
                src={recordWrapper.priority_level_relation.scope_pic}
                alt={recordWrapper.priority_level_relation.opt_display}
                style={{ width: 20, height: 20, marginRight: 8 }}
              />
              <span>{recordWrapper.priority_level_relation.opt_display}</span>
            </div>
          ),
        });
      }

      if (recordWrapper.user) {
        setSelectedUser({
          label: recordWrapper.user.name,
          value: recordWrapper.user.id,
        });
      }

      setFormState((prev) => ({
        ...prev,
        values: {
          ...prev.values,
          id: recordWrapper.id,
          action_title: recordWrapper.action_title,
          action_description: recordWrapper.action_description,
          user_id: recordWrapper.user_id,
          priority_level: recordWrapper.priority_level_relation?.id || "",
          implementation_time: parsedDate,
          // setIncidentTimezone: recordWrapper.incident_timezone || "",
        },
      }));
    }
  }, [incidentPreventionActionState.incidentPreventionActionRecord]);

  useEffect(() => {
    const severityLevel =
      incidentState.incidentCategoriesList?.Severity_Level || [];
    const formatted = severityLevel.map((item) => ({
      value: item.value,
      label: (
        <div style={{ display: "flex", alignItems: "center" }}>
          <img
            src={item.img || "/assets/images/file-icon.svg"}
            alt={item.label}
            style={{ width: 12, height: 12, marginRight: 8 }}
          />
          <span>{item.label}</span>
        </div>
      ),
    }));
    setSeverityLevelList(formatted);
  }, [incidentState.incidentCategoriesList]);

  useEffect(() => {
    const errors = validate(formState.values, schema);
    setFormState((formState) => ({
      ...formState,
      isValid: !errors,
      errors: errors || {},
    }));
  }, [formState.values]);

  useEffect(() => {
    if (incidentPreventionActionState.redirect_to_list) {
      setFormState({
        isValid: false,
        values: {
          object_viewed_id: session.current_page_permissions?.object_id || "",
        },
        touched: {
          object_viewed_id: true,
        },
        errors: {},
      });

      setSelectedUser();
      setSeverityLevelList([]);
      handleClose();
      dispatch(incidentPreventionActionListFetch());
    }
  }, [incidentPreventionActionState.redirect_to_list]);

  const userOptions = (userList || []).map((user) => ({
    label: user.name,
    value: user.id,
  }));

  const handleUserChange = (selectedOption) => {
    setSelectedUser(selectedOption);
    setFormState((prev) => ({
      ...prev,
      values: {
        ...prev.values,
        user_id: selectedOption?.value || "",
      },
    }));
  };

  const handleChange = (event) => {
    event.persist();
    setFormState((prev) => ({
      ...prev,
      values: {
        ...prev.values,
        [event.target.name]: event.target.value,
      },
      touched: {
        ...prev.touched,
        [event.target.name]: true,
      },
    }));
    dispatch(hideIncidentPreventionActionValidationError(event.target.name));
  };

  const handleSelectChange = (selectedOption, name) => {
    if (name === "priority_level") setPriorityLevelValue(selectedOption);

    setFormState((prev) => ({
      ...prev,
      values: {
        ...prev.values,
        [name]: selectedOption?.value || "",
      },
      touched: {
        ...prev.touched,
        [name]: true,
      },
    }));
  };

  const setDescription = (action_description) => {
    setFormState((formState) => ({
      ...formState,
      values: {
        ...formState.values,
        action_description: action_description,
      },
      touched: {
        ...formState.touched,
        action_description: true,
      },
    }));
    dispatch(hideIncidentPreventionActionValidationError("action_description"));
  };

  const handleIncidentDateTimeChange = (date) => {
    setIncidentDateTime(date);
    setFormState((prev) => ({
      ...prev,
      values: {
        ...prev.values,
        implementation_time: date,
      },
      touched: {
        ...prev.touched,
        implementation_time: true,
      },
    }));
  };

  const handleSubmit = async (event) => {
    event?.preventDefault();

    const payload = {
      incident_id: incidentId,
      ...formState.values,
      implementation_time: formState.values.implementation_time
        ? new Date(formState.values.implementation_time)
            .toISOString()
            .slice(0, 19)
        : null,
    };

    if (!isEmpty(incidentPreventionActionRecord)) {
      dispatch(updateIncidentPreventionAction(payload));
    } else {
      dispatch(addIncidentPreventionAction(payload));
    }

    setSelectedUser();
    setSeverityLevelList();
    setPriorityLevelValue();
  };

  const hasError = (field) =>
    formState.touched[field] && formState.errors[field];

  return (
    <>
      <div>
        <Modal
          show={show}
          onHide={handleClose}
          className="add-preventive-modal"
          centered
        >
          <div>
            <h1 className="font-18 font-weight-bold heading-color border-bottom pb-2">
              Add Preventive Action
            </h1>
          </div>

          <Modal.Body>
            <Row className="mb-3">
              <Col lg={6}>
                <Form.Group>
                  <Form.Label className="label-input">Title</Form.Label>
                  <Form.Control
                    type="text"
                    name="action_title"
                    value={formState.values.action_title || ""}
                    placeholder="Title"
                    className="input-field"
                    onChange={handleChange}
                    isInvalid={hasError("action_title")}
                  />
                  {hasError("action_title") && (
                    <Form.Control.Feedback type="invalid">
                      {formState.errors.action_title[0]}
                    </Form.Control.Feedback>
                  )}
                </Form.Group>
              </Col>
              <Col lg={6}>
                <Form.Group>
                  <Form.Label className="label-input">Proposed By</Form.Label>
                  <Select
                    placeholder="Select"
                    isSearchable
                    className="multi-select w-100"
                    // options={!isEmpty(userOptions) ? userOptions : []}
                    options={userOptions}
                    value={selectedUser}
                    onChange={handleUserChange}
                  />
                  {hasError("user_id") && (
                    <Form.Control.Feedback type="invalid">
                      {formState.errors.user_id?.[0]}
                    </Form.Control.Feedback>
                  )}
                </Form.Group>
              </Col>
              <Col lg={6}>
                <Form.Group>
                  <Form.Label className="label-input">
                    Implementation Timeline ({incidentTimezone})
                  </Form.Label>
                  <DatePicker
                    selected={incidentDateTime}
                    onChange={handleIncidentDateTimeChange}
                    showTimeSelect
                    timeFormat="HH:mm"
                    timeIntervals={1}
                    dateFormat="yyyy-MM-dd HH:mm"
                    timeCaption="Time"
                    className={`form-control input-field w-100 ${
                      hasError("implementation_time") ? "is-invalid" : ""
                    }`}
                    placeholderText="Select Date & Time"
                  />
                </Form.Group>
               
              </Col>
              <Col lg={6}>
                <Form.Group>
                  <Form.Label className="label-input">
                    Priority Level
                  </Form.Label>
                  <Select
                    options={severityLevelList}
                    value={priorityLevelValue}
                    onChange={(option) =>
                      handleSelectChange(option, "priority_level")
                    }
                    placeholder="Select Priority Level"
                    className="multi-select w-100"
                    isSearchable
                  />
                  {hasError("priority_level") && (
                    <Form.Control.Feedback type="invalid">
                      {formState.errors.priority_level?.[0]}
                    </Form.Control.Feedback>
                  )}
                </Form.Group>
              </Col>
            </Row>

            <Row className="mb-3">
              <Col lg={12}>
                <CKEditor
                  editor={ClassicEditor}
                  config={CK_CONFIGS(localStorage.getItem("token"))}
                  data={formState.values.action_description || ""}
                  onChange={(event, editor) => {
                    setDescription(editor.getData());
                  }}
                />
              </Col>
            </Row>

            <div className="d-flex justify-content-center gap-4 mt-4">
              <button
                className="n-u-btn"
                disabled={!formState.isValid}
                onClick={handleSubmit}
              >
                Create
              </button>
              <button className="c-b-btn" onClick={handleClose} type="button">
                Cancel
              </button>
            </div>
          </Modal.Body>
        </Modal>
      </div>
    </>
  );
};

export default AddPreventiveActionsModal;
